import { NA_ANSWERS, isPassFailType } from './assessment';

export const parseJSONString = (jsonStr) => {
  try {
    return JSON.parse(jsonStr);
  } catch (e) {
    return { error: 'Invalid JSON', raw: jsonStr };
  }
};

// Two different key shapes flow through the audit log, and neither is
// readable raw: call recording filenames encode
// CUST_..._AGENT_{name}_DT_{datetime}, while assignment/override keys encode
// CUST_{campaign}_TEMP_{template}_ASSIGN_{assignee}_JOB_{jobId}. Pull out
// whichever set of parts applies and keep the rest available but
// de-emphasized rather than dropping it.
export const parseCallFilename = (filename) => {
  const agentMatch = filename.match(/_AGENT_(.*?)(?:_|\.)/i);
  const dtMatch = filename.match(/_DT_(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})/i);
  const assignMatch = filename.match(/_ASSIGN_(.*?)_JOB_(.+)$/i);
  return {
    agent: agentMatch ? agentMatch[1].replace(/-/g, ' ') : null,
    callTime: dtMatch ? `${dtMatch[1]}T${dtMatch[2]}:${dtMatch[3]}:00` : null,
    assignee: assignMatch ? assignMatch[1].replace(/-/g, ' ') : null,
    jobId: assignMatch ? assignMatch[2] : null,
  };
};

export const formatTimestamp = (timestamp) => {
  return new Date(timestamp).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getStatusColor = (status) => {
  const colors = {
    passed: 'bg-green-100 text-green-800 border-green-200',
    failed: 'bg-red-100 text-red-800 border-red-200',
    success: 'bg-green-100 text-green-800 border-green-200',
    error: 'bg-red-100 text-red-800 border-red-200',
  };
  return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
};

export const getAnswerColor = (answer, type) => {
  // For Compliance and Conduct: "no" = green (no violation), "yes" = red (violation)
  // For Quality: "yes" = green (pass), "no" = red (fail)
  const inverted = isPassFailType(type);
  const normalised = answer?.toLowerCase();

  // A question that did not apply, or could not be determined, was never scored - it must never
  // read as a pass or a failure.
  if (NA_ANSWERS.includes(normalised)) {
    return 'bg-gray-100 text-gray-800 border-gray-200';
  }

  if (normalised === 'yes') {
    return inverted
      ? 'bg-red-100 text-red-800 border-red-200'
      : 'bg-green-100 text-green-800 border-green-200';
  } else if (normalised === 'no') {
    return inverted
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-red-100 text-red-800 border-red-200';
  }

  return 'bg-gray-100 text-gray-800 border-gray-200';
};

// Compare by step number rather than array position: the gate stops at the first step that
// does not hold, so a reviewer recording a later step can lengthen the list.
export const stepsDiffer = (oldSteps, newSteps) => {
  const byNumber = (steps) => {
    const map = {};
    (Array.isArray(steps) ? steps : []).forEach(step => {
      if (step && step.step !== undefined) map[step.step] = step;
    });
    return map;
  };

  const before = byNumber(oldSteps);
  const after = byNumber(newSteps);
  const numbers = new Set([...Object.keys(before), ...Object.keys(after)]);

  return Array.from(numbers).some(n =>
    (before[n]?.result ?? '') !== (after[n]?.result ?? '') ||
    (before[n]?.reason ?? '') !== (after[n]?.reason ?? '')
  );
};

export const detectChanges = (oldValue, newValue) => {
  const oldParsed = parseJSONString(oldValue);
  const newParsed = parseJSONString(newValue);

  const changes = [];

  if (oldParsed.status !== newParsed.status) {
    changes.push('status');
  }
  if (oldParsed.score !== newParsed.score) {
    changes.push('score');
  }
  if (oldParsed.answer !== newParsed.answer) {
    changes.push('answer');
  }
  if (oldParsed.reason !== newParsed.reason) {
    changes.push('reason');
  }

  // Applicability pre-check steps a reviewer corrected. Without this a steps-only override was
  // stored faithfully and then reported as unchanged - no "Changed" badge, and because this
  // same function feeds ItemCard's counter, "0 changes" in the list as well.
  if (stepsDiffer(oldParsed.applicabilitySteps, newParsed.applicabilitySteps)) {
    changes.push('steps');
  }

  return changes;
};
